import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useHabits } from "@/hooks/useHabits";
import { usePonds } from "@/hooks/usePonds";
import { useJoinRequests } from "@/hooks/useJoinRequests";
import { useNotifications } from "@/hooks/useNotifications";
import { useUserPreferences } from "@/hooks/useUserPreferences";
import { PondScene } from "@/components/pond/PondScene";
import { PondSelector } from "@/components/pond/PondSelector";
import { PondMapModal } from "@/components/pond/PondMapModal";
import { PublicPondsDialog } from "@/components/pond/PublicPondsDialog";
import { EditPondSheet } from "@/components/pond/EditPondSheet";
import { NotificationBell } from "@/components/notifications/NotificationBell";
import { NotificationListSheet } from "@/components/notifications/NotificationListSheet";
import { ProfileSheet } from "@/components/profile/ProfileSheet";
import { PWAInstallPrompt } from "@/components/prompts/PWAInstallPrompt";
import { NotificationPrompt } from "@/components/prompts/NotificationPrompt";
import { FrogEvolutionHelp } from "@/components/FrogEvolutionHelp";
import { StatsIndicators } from "@/components/header/StatsIndicators";
import { MobileMenu } from "@/components/header/MobileMenu";
import { AddHabitDialog, HabitFormData } from "@/components/habit/AddHabitDialog";
import { HabitDetailSheet } from "@/components/habit/HabitDetailSheet";
import AdultFrogCelebration from "@/components/subscription/AdultFrogCelebration";
import { Button } from "@/components/ui/button";
import { LogOut, User, Loader2, Map, Settings2 } from "lucide-react";

const Index = () => {
  const { user, loading: authLoading, signOut } = useAuth();
  const navigate = useNavigate();
  const { ponds, currentPond, currentPondId, setCurrentPondId, loading: pondsLoading, createPond, joinPond, updatePond, searchPublicPonds } = usePonds();
  const { habits, loading: habitsLoading, addHabit, completeHabit, hibernateHabit, deleteHabit, updateHabit, newAdultFrog, clearNewAdultFrog } = useHabits(currentPondId);
  const { requests: joinRequests, approveRequest, rejectRequest, refetch: refetchRequests } = useJoinRequests(currentPondId);
  const { notifications, unreadCount, markAsRead, markAllAsRead, deleteNotification, deleteAllNotifications } = useNotifications();
  const { preferences, loading: preferencesLoading } = useUserPreferences();
  
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [selectedHabitId, setSelectedHabitId] = useState<string | null>(null);
  const [detailSheetOpen, setDetailSheetOpen] = useState(false);
  const [pondMapOpen, setPondMapOpen] = useState(false);
  const [publicPondsOpen, setPublicPondsOpen] = useState(false);
  const [createPondDialogOpen, setCreatePondDialogOpen] = useState(false);
  const [profileSheetOpen, setProfileSheetOpen] = useState(false);
  const [notificationListOpen, setNotificationListOpen] = useState(false);
  const [pwaPromptOpen, setPwaPromptOpen] = useState(false);
  const [notificationPromptOpen, setNotificationPromptOpen] = useState(false);
  const [promptsChecked, setPromptsChecked] = useState(false);
  const [editPondOpen, setEditPondOpen] = useState(false);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/auth");
    }
  }, [user, authLoading, navigate]);

  // Check if we should show prompts after login
  useEffect(() => {
    if (!preferencesLoading && preferences && !promptsChecked) {
      setPromptsChecked(true);
      
      // Show PWA install prompt if not asked before
      if (preferences.shortcutPermission === "not_asked") {
        setTimeout(() => setPwaPromptOpen(true), 1000);
      }
      // Show notification prompt if not asked before (after PWA)
      else if (preferences.notificationPermission === "not_asked") {
        setTimeout(() => setNotificationPromptOpen(true), 1000);
      }
    }
  }, [preferences, preferencesLoading, promptsChecked]);

  // When PWA prompt closes, check if we should show notification prompt
  useEffect(() => {
    if (!pwaPromptOpen && promptsChecked && preferences?.notificationPermission === "not_asked") {
      setTimeout(() => setNotificationPromptOpen(true), 500);
    }
  }, [pwaPromptOpen, promptsChecked, preferences]);

  const selectedHabit = habits.find((h) => h.id === selectedHabitId);

  // Stats calculations
  const habitsCount = habits.length;
  const adultsCount = habits.filter((h) => h.frog.stage === "adult").length;
  const totalStreak = habits.reduce((acc, h) => acc + h.frog.currentStreak, 0);

  const handleHabitClick = (habitId: string) => {
    setSelectedHabitId(habitId);
    setDetailSheetOpen(true);
  };

  const handleAddHabit = async (data: HabitFormData) => {
    await addHabit(data);
  };

  const handleComplete = async (habitId: string, evidenceFile?: File) => {
    await completeHabit(habitId, evidenceFile);
  };

  const handleHibernate = async (habitId: string) => {
    await hibernateHabit(habitId);
    setDetailSheetOpen(false);
  };

  const handleDelete = async (habitId: string) => {
    await deleteHabit(habitId);
    setDetailSheetOpen(false);
  };

  const handleUpdate = async (habitId: string, data: HabitFormData) => {
    await updateHabit(habitId, data);
  };

  const handleNotificationClick = async (notification: any) => {
    await markAsRead(notification.id);
    if (notification.type === "join_request") {
      refetchRequests();
    }
    // Navigate to pond if data contains pond_id
    if (notification.data?.pond_id) {
      setCurrentPondId(notification.data.pond_id);
    }
    setNotificationListOpen(false);
  };

  const handleApproveRequest = async (requestId: string) => {
    const success = await approveRequest(requestId);
    return success;
  };

  const handleRejectRequest = async (requestId: string) => {
    const success = await rejectRequest(requestId);
    return success;
  };

  if (authLoading || habitsLoading || pondsLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Carregando sua lagoa...</p>
        </div>
      </div>
    );
  }

  if (!user) return null;

  // Transform habits for PondScene
  const pondHabits = habits.map((h) => ({
    id: h.id,
    name: h.name,
    frog: {
      stage: h.frog.stage,
      daysCompleted: h.frog.daysCompleted,
      currentStreak: h.frog.currentStreak,
      positionX: h.frog.positionX,
      positionY: h.frog.positionY,
    },
  }));

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-md border-b border-border">
        <div className="container mx-auto px-3 sm:px-4 py-3 flex items-center justify-between gap-2">
          {/* Left side - Logo and pond selector */}
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-9 h-9 bg-lily-pad rounded-full flex items-center justify-center shadow-soft flex-shrink-0">
              <span className="text-lg">🐸</span>
            </div>
            
            {/* Pond Map Button */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setPondMapOpen(true)}
              className="gap-1.5 font-display px-2"
            >
              <Map className="h-4 w-4" />
              <span className="max-w-[100px] truncate">
                {currentPond?.name || "Lagoas"}
              </span>
            </Button>

            {/* Edit Pond Button - only for owners */}
            {currentPond?.isOwner && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setEditPondOpen(true)}
                className="h-8 w-8"
                title="Editar lagoa"
              >
                <Settings2 className="h-4 w-4" />
              </Button>
            )}
          </div>

          {/* Center - Stats (desktop only) */}
          <div className="hidden md:flex items-center">
            <StatsIndicators
              habitsCount={habitsCount}
              adultsCount={adultsCount}
              totalStreak={totalStreak}
            />
          </div>

          {/* Right side - Desktop: notifications + actions, Mobile: hamburger */}
          <div className="flex items-center gap-1 flex-shrink-0">
            {/* Desktop actions */}
            <div className="hidden md:flex items-center gap-1">
              <NotificationBell
                notifications={notifications}
                unreadCount={unreadCount}
                onNotificationClick={() => setNotificationListOpen(true)}
                onMarkAllRead={markAllAsRead}
              />
              <Button variant="ghost" size="icon" className="h-9 w-9" onClick={() => setProfileSheetOpen(true)}>
                <User className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" className="h-9 w-9" onClick={signOut}>
                <LogOut className="h-4 w-4" />
              </Button>
            </div>

            {/* Mobile hamburger menu */}
            <MobileMenu
              habitsCount={habitsCount}
              adultsCount={adultsCount}
              totalStreak={totalStreak}
              unreadNotifications={unreadCount}
              onOpenPondMap={() => setPondMapOpen(true)}
              onOpenProfile={() => setProfileSheetOpen(true)}
              onSignOut={signOut}
              onOpenNotifications={() => setNotificationListOpen(true)}
            />
          </div>
        </div>
      </header>

      {/* Main content - Pond takes full remaining height */}
      <main className="flex-1 container mx-auto px-3 sm:px-4 py-3 sm:py-4 flex flex-col overflow-hidden">
        {/* Help icon */}
        <div className="flex justify-start mb-2 flex-shrink-0">
          <FrogEvolutionHelp />
        </div>

        {/* Pond Scene - takes all available space */}
        <div className="flex-1 relative" style={{ minHeight: '400px' }}>
          <div className="absolute inset-0">
            <PondScene
              habits={pondHabits}
              joinRequests={joinRequests}
              isOwner={currentPond?.isOwner || false}
              onHabitClick={handleHabitClick}
              onAddHabit={() => setAddDialogOpen(true)}
              onApproveRequest={handleApproveRequest}
              onRejectRequest={handleRejectRequest}
            />
          </div>
        </div>
      </main>

      {/* Pond Map Modal */}
      <PondMapModal
        open={pondMapOpen}
        onOpenChange={setPondMapOpen}
        ponds={ponds}
        currentPondId={currentPondId}
        onSelectPond={(pondId) => {
          setCurrentPondId(pondId);
          setPondMapOpen(false);
        }}
        onCreatePond={() => {
          setPondMapOpen(false);
          setCreatePondDialogOpen(true);
        }}
        onExplorePublic={() => {
          setPondMapOpen(false);
          setPublicPondsOpen(true);
        }}
        onJoinPond={joinPond}
      />

      {/* Public Ponds Dialog */}
      <PublicPondsDialog
        open={publicPondsOpen}
        onOpenChange={setPublicPondsOpen}
        onSearch={searchPublicPonds}
        onJoinPond={async (pondId) => {}}
      />

      {/* Pond Selector for creating ponds */}
      <PondSelector
        ponds={ponds}
        currentPond={currentPond}
        onSelectPond={setCurrentPondId}
        onCreatePond={async (name, isPublic, parentId, requiresApproval, notifyOnJoin) => {
          const result = await createPond(name, isPublic, parentId, requiresApproval, notifyOnJoin);
          setCreatePondDialogOpen(false);
          return result;
        }}
        onJoinPond={joinPond}
        onSearchPublicPonds={searchPublicPonds}
        showAsDropdown={false}
        defaultOpenCreate={createPondDialogOpen}
      />

      {/* Dialogs */}
      <AddHabitDialog
        open={addDialogOpen}
        onOpenChange={setAddDialogOpen}
        onSubmit={handleAddHabit}
      />

      <HabitDetailSheet
        open={detailSheetOpen}
        onOpenChange={setDetailSheetOpen}
        habit={selectedHabit ? {
          id: selectedHabit.id,
          name: selectedHabit.name,
          frequencyType: selectedHabit.frequencyType,
          frequencyCount: selectedHabit.frequencyCount,
          evidenceType: selectedHabit.evidenceType,
          isPrivate: true,
          locationLat: selectedHabit.locationLat,
          locationLng: selectedHabit.locationLng,
          locationRadius: selectedHabit.locationRadius,
          scheduledTime: selectedHabit.scheduledTime,
          timezone: selectedHabit.timezone,
          frog: selectedHabit.frog,
        } : null}
        onComplete={handleComplete}
        onHibernate={handleHibernate}
        onDelete={handleDelete}
        onUpdate={handleUpdate}
      />

      {/* Adult Frog Celebration Popup */}
      <AdultFrogCelebration
        isOpen={!!newAdultFrog}
        onClose={clearNewAdultFrog}
        habitName={newAdultFrog?.habitName || ""}
        adultFrogCount={newAdultFrog?.adultCount || 1}
      />

      {/* Profile Sheet */}
      <ProfileSheet
        open={profileSheetOpen}
        onOpenChange={setProfileSheetOpen}
      />

      {/* Notification List Sheet */}
      <NotificationListSheet
        open={notificationListOpen}
        onOpenChange={setNotificationListOpen}
        notifications={notifications}
        onNotificationClick={handleNotificationClick}
        onMarkAsRead={markAsRead}
        onMarkAllAsRead={markAllAsRead}
        onDeleteNotification={deleteNotification}
        onDeleteAllNotifications={deleteAllNotifications}
      />

      {/* PWA Install Prompt */}
      <PWAInstallPrompt
        open={pwaPromptOpen}
        onOpenChange={setPwaPromptOpen}
      />

      {/* Notification Prompt */}
      <NotificationPrompt
        open={notificationPromptOpen}
        onOpenChange={setNotificationPromptOpen}
      />

      {/* Edit Pond Sheet */}
      <EditPondSheet
        open={editPondOpen}
        onOpenChange={setEditPondOpen}
        pond={currentPond ? {
          ...currentPond,
          inviteCode: currentPond.inviteCode || null,
        } : null}
        onUpdate={updatePond}
      />
    </div>
  );
};

export default Index;
